import type {
  RegulationsListSlice,
  RegulationsListState,
  SliceCreator,
} from "../types";
import type { GridFilterModel } from "@mui/x-data-grid-premium";
import { GridLogicOperator } from "@mui/x-data-grid-premium";

const DEFAULT_GRID_DENSITY = "standard" as const;

const emptyGridColumnFilterModel = (): GridFilterModel => ({
  items: [],
  logicOperator: GridLogicOperator.And,
});

export const regulationsListInitialState = (): RegulationsListState => ({
  regulationsList: {
    paginationModel: { page: 0, pageSize: 10 },
    selectedMarkets: [],
    selectedProductTypes: [],
    selectedHA: [],
    selectedTags: [],
    selectedTitle: "",
    fromDateIso: null,
    toDateIso: null,
    favoriteFilter: null,
    activeSavedFilterId: null,
    gridColumnFilterModel: emptyGridColumnFilterModel(),
    gridColumnVisibilityModel: {},
    gridColumnOrderedFields: [],
    gridDensity: DEFAULT_GRID_DENSITY,
  },
});

export const createRegulationsListSlice: SliceCreator<RegulationsListSlice> = (
  set
) => ({
  ...regulationsListInitialState(),
  setRegulationsListPagination: (model) =>
    set(
      (state) => ({
        regulationsList: {
          ...state.regulationsList,
          paginationModel: model,
        },
      }),
      false,
      "regulationsList/setPagination"
    ),
  setRegulationsListMarkets: (markets) =>
    set(
      (state) => ({
        regulationsList: {
          ...state.regulationsList,
          selectedMarkets: markets,
        },
      }),
      false,
      "regulationsList/setMarkets"
    ),
  setRegulationsListProductTypes: (items) =>
    set(
      (state) => ({
        regulationsList: {
          ...state.regulationsList,
          selectedProductTypes: items,
        },
      }),
      false,
      "regulationsList/setProductTypes"
    ),
  setRegulationsListHA: (items) =>
    set(
      (state) => ({
        regulationsList: {
          ...state.regulationsList,
          selectedHA: items,
        },
      }),
      false,
      "regulationsList/setHA"
    ),
  setRegulationsListTags: (items) =>
    set(
      (state) => ({
        regulationsList: {
          ...state.regulationsList,
          selectedTags: items,
        },
      }),
      false,
      "regulationsList/setTags"
    ),
  setRegulationsListTitle: (title) =>
    set(
      (state) => ({
        regulationsList: {
          ...state.regulationsList,
          selectedTitle: title,
        },
      }),
      false,
      "regulationsList/setTitle"
    ),
  setRegulationsListDateRange: (fromIso, toIso) =>
    set(
      (state) => ({
        regulationsList: {
          ...state.regulationsList,
          fromDateIso: fromIso,
          toDateIso: toIso,
        },
      }),
      false,
      "regulationsList/setDateRange"
    ),
  setRegulationsListFavoriteFilter: (value) =>
    set(
      (state) => ({
        regulationsList: {
          ...state.regulationsList,
          favoriteFilter: value,
        },
      }),
      false,
      "regulationsList/setFavoriteFilter"
    ),
  setRegulationsListActiveSavedFilterId: (id) =>
    set(
      (state) => ({
        regulationsList: {
          ...state.regulationsList,
          activeSavedFilterId: id,
        },
      }),
      false,
      "regulationsList/setActiveSavedFilterId"
    ),
  setRegulationsListGridColumnFilterModel: (model) =>
    set(
      (state) => ({
        regulationsList: {
          ...state.regulationsList,
          gridColumnFilterModel: model,
        },
      }),
      false,
      "regulationsList/setGridColumnFilterModel"
    ),
  setRegulationsListGridColumnVisibilityModel: (model) =>
    set(
      (state) => ({
        regulationsList: {
          ...state.regulationsList,
          gridColumnVisibilityModel: model,
        },
      }),
      false,
      "regulationsList/setGridColumnVisibilityModel"
    ),
  setRegulationsListGridColumnOrderedFields: (fields) =>
    set(
      (state) => ({
        regulationsList: {
          ...state.regulationsList,
          gridColumnOrderedFields: fields,
        },
      }),
      false,
      "regulationsList/setGridColumnOrderedFields"
    ),
  setRegulationsListGridDensity: (density) =>
    set(
      (state) => ({
        regulationsList: {
          ...state.regulationsList,
          gridDensity: density,
        },
      }),
      false,
      "regulationsList/setGridDensity"
    ),
  resetRegulationsListFilters: () =>
    set(
      (state) => ({
        regulationsList: {
          ...state.regulationsList,
          selectedMarkets: [],
          selectedProductTypes: [],
          selectedHA: [],
          selectedTags: [],
          selectedTitle: "",
          fromDateIso: null,
          toDateIso: null,
          favoriteFilter: null,
          activeSavedFilterId: null,
          gridColumnFilterModel: emptyGridColumnFilterModel(),
          paginationModel: { ...state.regulationsList.paginationModel, page: 0 },
        },
      }),
      false,
      "regulationsList/resetFilters"
    ),
});
